import React, { FC, useMemo, useState } from 'react'
import styled from 'styled-components'
import DonutIndicator, { DonutItem } from './DonutIndicator'
import { EmptyState } from './styles'
import UtmBuilderModal from './UtmBuilderModal'
import { formatCurrencyBRL } from '../../utils/format-currency-without-rs'

/**
 * Origem das vendas (Cockpit de Campanhas — GA4).
 *
 * Mostra o split por CANAL DE VENDA (Online × Física) do banco e, para a venda
 * ONLINE, a origem por canal de tráfego (mix do GA4 já reconciliado sobre a
 * receita online). A venda física (bilheteria/PDV) não tem origem online — é
 * bucket próprio. O seletor Todos/Online/Física é presentacional (sem refetch).
 */

interface Channel {
  canal: string
  label: string
  receitaMix: number
  receita: number
  ingressos: number
}

interface Campaign {
  campanha: string
  canal: string
  receita: number
  ingressos: number
  ticketMedio: number
}

interface SalesOriginData {
  total?: { receita: number; ingressos: number }
  online?: { receita: number; ingressos: number; pct: number }
  fisica?: { receita: number; ingressos: number; pct: number }
  coberturaGA4Online?: number
  coberturaCampanhaOnline?: number
  lojaUrl?: string | null
  canais?: Channel[]
  campanhas?: Campaign[]
}

type View = 'todos' | 'online' | 'fisica' | 'campanhas'

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const TilesRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`

const Tile = styled.div<{ $accent?: string }>`
  flex: 1 1 160px;
  min-width: 150px;
  background: #F9F9F9;
  border-radius: 12px;
  padding: 14px 16px;
  border-left: 3px solid ${(p) => p.$accent ?? '#212121'};
  display: flex;
  flex-direction: column;
  gap: 3px;
`

const TileLabel = styled.span`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #B0B0B0;
`

const TileVal = styled.span<{ $fs?: number }>`
  font-family: 'Inter', sans-serif;
  font-size: ${(p) => p.$fs ?? 20}px;
  font-weight: 700;
  color: #212121;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  line-height: 1.15;
`

const TileSub = styled.span`
  font-family: 'Inter', sans-serif;
  font-size: 12.5px;
  color: #757575;
`

const Highlights = styled.ul`
  list-style: none;
  margin: 0;
  padding: 14px 16px;
  background: #F9F9F9;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Highlight = styled.li`
  display: flex;
  gap: 9px;
  align-items: flex-start;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: #333333;
  line-height: 1.45;
`

const HlIcon = styled.span`
  flex-shrink: 0;
  font-size: 14px;
  line-height: 1.4;
`

const Segmented = styled.div`
  display: inline-flex;
  align-self: flex-start;
  background: #f2f4f7;
  border-radius: 10px;
  padding: 3px;
  gap: 2px;
`

const SegBtn = styled.button<{ $active: boolean }>`
  border: none;
  cursor: pointer;
  border-radius: 8px;
  padding: 7px 16px;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${(p) => (p.$active ? '#212121' : '#757575')};
  background: ${(p) => (p.$active ? '#ffffff' : 'transparent')};
  box-shadow: ${(p) => (p.$active ? '0 1px 2px rgba(16,24,40,0.12)' : 'none')};
`

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
`

const SectionLabel = styled.span`
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #555555;
`

/* Ícone "?" com tooltip explicando os canais (glossário GA4) */
const InfoWrap = styled.span`
  position: relative;
  display: inline-flex;
`

const InfoIcon = styled.button`
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  border-radius: 50%;
  cursor: help;
  background: #e4e7ec;
  color: #555555;
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover,
  &:focus {
    background: #212121;
    color: #ffffff;
    outline: none;
  }
`

const Tip = styled.div`
  position: absolute;
  top: 24px;
  left: -8px;
  z-index: 50;
  width: 340px;
  max-width: 82vw;
  background: #ffffff;
  border: 1px solid #eef2f6;
  border-radius: 12px;
  box-shadow: 0 12px 28px rgba(16, 24, 40, 0.16);
  padding: 14px 16px;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-4px);
  transition: opacity 0.15s ease, transform 0.15s ease;

  ${InfoWrap}:hover &,
  ${InfoWrap}:focus-within & {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }
`

const TipTitle = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #212121;
  margin-bottom: 10px;
`

const TipList = styled.dl`
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 9px;
`

const TipItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`

const TipTerm = styled.dt`
  font-family: 'Inter', sans-serif;
  font-size: 12.5px;
  font-weight: 600;
  color: #333333;
`

const TipDesc = styled.dd`
  margin: 0;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: #757575;
  line-height: 1.4;
`

const TipText = styled.p`
  margin: 0 0 9px;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: #757575;
  line-height: 1.5;

  &:last-child {
    margin-bottom: 0;
  }
`

/* -------- Campanhas (Fase 2) -------- */
const CampHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`

const CoverageNote = styled.div`
  flex: 1;
  min-width: 240px;
  font-family: 'Inter', sans-serif;
  font-size: 12.5px;
  color: #555555;
  background: #F9F9F9;
  border-radius: 10px;
  padding: 10px 14px;
`

const BuilderBtn = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: none;
  border-radius: 9px;
  padding: 9px 16px;
  background: #B0020B;
  color: #fff;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #8A0109;
  }
`

const CampList = styled.div`
  display: flex;
  flex-direction: column;
`

const CampRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #f2f4f7;

  &:last-child {
    border-bottom: none;
  }
`

const CampMain = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const CampName = styled.span`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #212121;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const CampChip = styled.span`
  align-self: flex-start;
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #555555;
  background: #eef2f6;
  padding: 2px 8px;
  border-radius: 999px;
`

const CampMetrics = styled.div`
  display: flex;
  gap: 22px;
  flex-shrink: 0;
`

const CampMetric = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 1px;
`

const CampVal = styled.span`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #212121;
  font-variant-numeric: tabular-nums;
`

const CampMetricLabel = styled.span`
  font-family: 'Inter', sans-serif;
  font-size: 10.5px;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #B0B0B0;
`

/** Glossário dos channel groups do GA4 (o que cada canal significa). */
const CHANNEL_GLOSSARY: { label: string; desc: string }[] = [
  { label: 'Direto', desc: 'Entrou digitando o endereço, por favorito ou sem origem identificável (app, link sem rastreio).' },
  { label: 'Social orgânico', desc: 'Redes sociais sem anúncio pago — post, bio, stories, grupos.' },
  { label: 'Social pago', desc: 'Anúncio pago em redes sociais (Meta, Instagram, TikTok Ads).' },
  { label: 'Busca orgânica', desc: 'Resultado não pago em buscadores (Google, Bing).' },
  { label: 'Busca paga', desc: 'Anúncio pago em buscadores (Google Ads / links patrocinados).' },
  { label: 'Referência', desc: 'Link em outro site — blog, portal ou agregador de eventos.' },
  { label: 'E-mail', desc: 'Link em campanha de e-mail.' },
  { label: 'Cross-network', desc: 'Google Performance Max / Demand Gen — anúncios automáticos exibidos ao mesmo tempo em YouTube, Busca, Gmail e Display.' },
  { label: 'AI Assistant', desc: 'Assistentes de IA (ChatGPT e similares) — canal novo do GA4.' },
  { label: 'Não atribuído', desc: 'O GA4 não conseguiu identificar a origem da sessão.' },
]

const Note = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: #B0B0B0;
  margin: 0;
`

const pctLabel = (p: number): string => `${(Number(p) * 100).toFixed(0)}%`
const intBR = (n: number): string => (Number(n) || 0).toLocaleString('pt-BR')

/** Moeda compacta p/ caber em cards estreitos: R$ 16,2 mi / R$ 743 mil / R$ 999,00. */
const formatCompactBRL = (v: number): string => {
  const n = Number(v) || 0
  if (n >= 1e6)
    return `R$ ${(n / 1e6).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 2 })} mi`
  if (n >= 1e5) return `R$ ${(n / 1e3).toLocaleString('pt-BR', { maximumFractionDigits: 0 })} mil`
  return formatCurrencyBRL(n)
}

/** Fonte do valor do tile responsiva ao tamanho do número — cabe milhões nos cards
 * estreitos do comparativo sem estourar; números curtos ficam grandes. */
const fitFontPx = (len: number): number => {
  if (len <= 13) return 20 // até R$ 999.999,00
  if (len <= 15) return 18
  if (len <= 17) return 16 // milhões (R$ 12.364.225,00)
  if (len <= 20) return 14 // dezenas/centenas de milhões
  return 12
}

/** Rosca de canais na escala preto→cinza da dashboard (mesma da rosca de Estados). */
const CHANNEL_GRAYSCALE = [
  '#212121',
  '#3F3F3F',
  '#5C5C5C',
  '#787878',
  '#949494',
  '#B0B0B0',
  '#CCCCCC',
  '#E0E0E0',
]

const SalesOrigin: FC<{ data: SalesOriginData; compact?: boolean }> = ({ data, compact }) => {
  const [view, setView] = useState<View>('todos')

  const total = data?.total ?? { receita: 0, ingressos: 0 }
  const online = data?.online ?? { receita: 0, ingressos: 0, pct: 0 }
  const fisica = data?.fisica ?? { receita: 0, ingressos: 0, pct: 0 }

  const donutItems: DonutItem[] = useMemo(() => {
    const canais = Array.isArray(data?.canais) ? (data!.canais as Channel[]) : []
    return canais.map((c, i) => ({
      label: c.label,
      value: c.receitaMix,
      quantity: c.ingressos,
      color: CHANNEL_GRAYSCALE[i % CHANNEL_GRAYSCALE.length],
    }))
  }, [data])

  const campanhas = Array.isArray(data?.campanhas) ? (data!.campanhas as Campaign[]) : []
  const coberturaCampanha = Number(data?.coberturaCampanhaOnline ?? 0)
  const lojaUrl = typeof data?.lojaUrl === 'string' ? data.lojaUrl : ''
  const [showBuilder, setShowBuilder] = useState(false)

  // Destaques automáticos: frases em linguagem simples derivadas dos dados.
  const insights = useMemo(() => {
    const out: { icon: string; text: string }[] = []
    const canaisArr = Array.isArray(data?.canais) ? (data!.canais as Channel[]) : []
    const fisicaPct = Number(data?.fisica?.pct ?? 0)
    const cobCamp = Number(data?.coberturaCampanhaOnline ?? 0)

    if (canaisArr.length) {
      const top = canaisArr[0]
      out.push({
        icon: '🏆',
        text: `${top.label} é a maior origem online: ${top.receitaMix.toFixed(0)}% (${formatCurrencyBRL(
          top.receita
        )}).`,
      })
    }
    const tickets = canaisArr
      .filter((c) => c.ingressos >= 10)
      .map((c) => ({ label: c.label, tm: c.receita / c.ingressos }))
      .sort((a, b) => b.tm - a.tm)
    if (tickets.length > 1) {
      out.push({
        icon: '💰',
        text: `Maior ticket médio: ${tickets[0].label} (${formatCurrencyBRL(tickets[0].tm)}).`,
      })
    }
    if (fisicaPct >= 0.25) {
      out.push({
        icon: '🎫',
        text: `${(fisicaPct * 100).toFixed(0)}% das vendas são presenciais (bilheteria/PDV).`,
      })
    }
    if (canaisArr.length && cobCamp < 0.3) {
      out.push({
        icon: '🔗',
        text: `Só ${(cobCamp * 100).toFixed(
          0
        )}% do online tem campanha marcada — use o gerador de link para medir as suas.`,
      })
    }
    return out.slice(0, 3)
  }, [data])

  // no comparativo (cards estreitos) usamos formato compacto (R$ 16,2 mi) p/ não estourar
  const fmtVal = compact ? formatCompactBRL : formatCurrencyBRL
  const totalStr = fmtVal(total.receita)
  const onlineStr = fmtVal(online.receita)
  const fisicaStr = fmtVal(fisica.receita)
  // fonte uniforme nos 3 tiles, ditada pelo maior número (mantém consistência e cabe)
  const tileFs = fitFontPx(Math.max(totalStr.length, onlineStr.length, fisicaStr.length))

  return (
    <Wrap>
      <TilesRow>
        <Tile>
          <TileLabel>Total</TileLabel>
          <TileVal $fs={tileFs} title={formatCurrencyBRL(total.receita)}>{totalStr}</TileVal>
          <TileSub>{intBR(total.ingressos)} ingressos</TileSub>
        </Tile>
        <Tile $accent="#B0020B">
          <TileLabel>Online (loja)</TileLabel>
          <TileVal $fs={tileFs} title={formatCurrencyBRL(online.receita)}>{onlineStr}</TileVal>
          <TileSub>{pctLabel(online.pct)} do total · {intBR(online.ingressos)} ing</TileSub>
        </Tile>
        <Tile $accent="#B0B0B0">
          <TileLabel>Física (bilheteria)</TileLabel>
          <TileVal $fs={tileFs} title={formatCurrencyBRL(fisica.receita)}>{fisicaStr}</TileVal>
          <TileSub>{pctLabel(fisica.pct)} do total · {intBR(fisica.ingressos)} ing</TileSub>
        </Tile>
      </TilesRow>

      {insights.length > 0 && (
        <Highlights>
          {insights.map((h, i) => (
            <Highlight key={i}>
              <HlIcon>{h.icon}</HlIcon>
              <span>{h.text}</span>
            </Highlight>
          ))}
        </Highlights>
      )}

      <Segmented>
        {(['todos', 'online', 'fisica', 'campanhas'] as View[]).map((v) => (
          <SegBtn key={v} $active={view === v} type="button" onClick={() => setView(v)}>
            {v === 'todos'
              ? 'Todos'
              : v === 'online'
              ? 'Online'
              : v === 'fisica'
              ? 'Física'
              : 'Campanhas'}
          </SegBtn>
        ))}
      </Segmented>

      {view === 'fisica' ? (
        <EmptyState>
          Venda presencial (bilheteria/PDV): {formatCurrencyBRL(fisica.receita)} ·{' '}
          {intBR(fisica.ingressos)} ingressos.
          <br />
          A origem por canal não se aplica à venda física.
        </EmptyState>
      ) : view === 'campanhas' ? (
        campanhas.length > 0 ? (
          <>
            <CampHeader>
              <CoverageNote>
                {(coberturaCampanha * 100).toFixed(0)}% da venda online tem campanha identificada
                (UTM). O restante é tráfego sem marcação. Campanhas <strong>[BD]</strong> são da
                Bilheteria — marque seus links com <strong>utm_campaign</strong> para medir as suas.
              </CoverageNote>
              <InfoWrap>
                <InfoIcon type="button" aria-label="Como ler as campanhas">
                  ?
                </InfoIcon>
                <Tip role="tooltip">
                  <TipTitle>Como ler as campanhas</TipTitle>
                  <TipText>
                    O <strong>ingresso</strong> é sempre do seu evento. A <strong>campanha</strong>{' '}
                    mostra o que trouxe o comprador ao site — às vezes um anúncio de outro evento ou
                    institucional da Bilheteria trouxe alguém que acabou comprando o seu (um
                    “assist”).
                  </TipText>
                  <TipText>
                    Campanhas <strong>[BD]</strong> e <strong>bd-…</strong> são da Bilheteria. Para
                    ver as suas, marque seus links com UTM (botão ao lado).
                  </TipText>
                  <TipText>
                    <strong>Cross-network</strong> = Performance Max do Google (anúncios automáticos
                    em YouTube, Busca, Gmail e Display).
                  </TipText>
                </Tip>
              </InfoWrap>
              {lojaUrl && (
                <BuilderBtn type="button" onClick={() => setShowBuilder(true)}>
                  🔗 Gerar link rastreável
                </BuilderBtn>
              )}
            </CampHeader>
            <CampList>
              {campanhas.map((c, i) => (
                <CampRow key={`${c.campanha}-${i}`}>
                  <CampMain>
                    <CampName title={c.campanha}>{c.campanha}</CampName>
                    <CampChip>{c.canal}</CampChip>
                  </CampMain>
                  <CampMetrics>
                    <CampMetric>
                      <CampVal>{formatCurrencyBRL(c.receita)}</CampVal>
                      <CampMetricLabel>Receita</CampMetricLabel>
                    </CampMetric>
                    <CampMetric>
                      <CampVal>{intBR(c.ingressos)}</CampVal>
                      <CampMetricLabel>Ingressos</CampMetricLabel>
                    </CampMetric>
                    <CampMetric>
                      <CampVal>{formatCurrencyBRL(c.ticketMedio)}</CampVal>
                      <CampMetricLabel>Ticket médio</CampMetricLabel>
                    </CampMetric>
                  </CampMetrics>
                </CampRow>
              ))}
            </CampList>
          </>
        ) : (
          <EmptyState>
            Nenhuma campanha identificada no período.
            <br />
            Marque seus links com <strong>utm_campaign</strong> (UTM) para medir suas campanhas aqui.
            {lojaUrl && (
              <div style={{ marginTop: 16 }}>
                <BuilderBtn type="button" onClick={() => setShowBuilder(true)}>
                  🔗 Gerar link rastreável
                </BuilderBtn>
              </div>
            )}
          </EmptyState>
        )
      ) : donutItems.length > 0 ? (
        <>
          <LabelRow>
            <SectionLabel>Origem da venda online por canal</SectionLabel>
            <InfoWrap>
              <InfoIcon type="button" aria-label="O que é cada canal">
                ?
              </InfoIcon>
              <Tip role="tooltip">
                <TipTitle>O que é cada canal</TipTitle>
                <TipList>
                  {CHANNEL_GLOSSARY.map((g) => (
                    <TipItem key={g.label}>
                      <TipTerm>{g.label}</TipTerm>
                      <TipDesc>{g.desc}</TipDesc>
                    </TipItem>
                  ))}
                </TipList>
              </Tip>
            </InfoWrap>
          </LabelRow>
          <DonutIndicator items={donutItems} showQuantity />
          <Note>
            Base: GA4 sobre a venda online. A proporção por canal é aplicada à receita online do
            painel (a venda física não é rastreada por canal).
          </Note>
        </>
      ) : (
        <EmptyState>
          {online.receita > 0
            ? 'A origem por canal está sendo atualizada (GA4) — os totais de venda acima já estão corretos. Tente novamente em alguns minutos.'
            : 'Sem venda online no período selecionado.'}
        </EmptyState>
      )}

      {showBuilder && lojaUrl && (
        <UtmBuilderModal lojaUrl={lojaUrl} onClose={() => setShowBuilder(false)} />
      )}
    </Wrap>
  )
}

export default SalesOrigin
